import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { brandDnaApi } from '@/services/api/brandDnaApi';
import { knowledgeBaseApi } from '@/services/api/knowledgeBaseApi';
import { competitorApi } from '@/services/api/competitorApi';
import { postsApi } from '@/services/api/postsApi';
import { chatApi } from '@/services/api/chatApi';
import { telegramApi } from '@/services/api/telegramApi';
import { EMPTY_SETUP_SIGNALS, SETUP_STEP_IDS, SIGNAL_KEY_BY_STEP } from '@/constants/setupSteps';
import { isStepComplete, isStepEnabled } from '@/utils/setupProgress';
import type { SetupSignals, SetupStepId } from '@/types';

/**
 * Post statuses a post can only reach because the user acted on it in the
 * review queue. `pending_approval` and `draft` are deliberately absent — those
 * are states the generator leaves posts in, not evidence of a review.
 */
const REVIEWED_POST_STATUSES = ['approved', 'scheduled', 'published', 'rejected'] as const;

/** Backoff for confirming an action the backend completes asynchronously. */
const CONFIRM_RETRY_DELAYS_MS = [2_000, 5_000, 10_000];

/** A revalidation younger than this is reused instead of re-hitting the API. */
export const SETUP_STALE_AFTER_MS = 15_000;

type FetchStatus = 'idle' | 'loading' | 'ready' | 'error';

/** Reads one step's signal from the backend. Throws on failure. */
const SIGNAL_FETCHERS: Record<SetupStepId, () => Promise<Partial<SetupSignals>>> = {
  'brand-dna': async () => {
    const aggregate = await brandDnaApi.getAggregate();
    return { brandDnaPercent: aggregate.progress?.percent ?? 0 };
  },
  'knowledge-base': async () => {
    const docs = await knowledgeBaseApi.list();
    const indexed = Array.isArray(docs) ? docs.filter(d => d.status === 'indexed').length : 0;
    return { knowledgeBaseIndexedCount: indexed };
  },
  competitors: async () => {
    const accounts = await competitorApi.listCompetitors();
    return { competitorCount: Array.isArray(accounts) ? accounts.length : 0 };
  },
  'review-queue': async () => {
    const stats = await postsApi.getStats();
    const byStatus = stats?.byStatus ?? {};
    const reviewed = REVIEWED_POST_STATUSES.reduce((sum, s) => sum + (byStatus[s] ?? 0), 0);
    return { reviewedPostCount: reviewed };
  },
  chat: async () => {
    // `general_chat` only — the onboarding interview also creates conversations,
    // and crediting those would mark the step done before the user ever opened
    // the chat page.
    const res = await chatApi.listConversations({ source: 'general_chat', limit: 20 });
    const list = res?.conversations ?? res?.data ?? [];
    return { chatConversationCount: list.filter(c => (c.messageCount ?? 0) > 0).length };
  },
  telegram: async () => {
    const status = await telegramApi.getStatus();
    return { telegramLinked: status.linked };
  },
};

/**
 * Stand-in value for a step's signal when the real one isn't available: the
 * optimistic flip needs one that reads as complete, a failed read of a
 * never-fetched signal needs one that reads as incomplete. Telegram is a
 * boolean rather than a count, so a blanket `1` / `0` would not satisfy its
 * strict `=== true` predicate.
 */
function placeholderSignal(step: SetupStepId, complete: boolean): number | boolean {
  if (step === 'telegram') return complete;
  // Brand DNA is a server-computed percent, complete only at exactly 100.
  if (step === 'brand-dna') return complete ? 100 : 0;
  // Counts: every remaining predicate is a "> 0" test, so 1 is enough.
  return complete ? 1 : 0;
}

interface SetupState {
  signals: SetupSignals;
  status: Record<SetupStepId, FetchStatus>;
  /** Epoch ms of the last successful read per step, for the staleness guard. */
  fetchedAt: Record<SetupStepId, number | null>;
}

interface SetupActions {
  /** Revalidate specific steps (default: all). `force` skips the staleness guard. */
  refresh: (steps?: readonly SetupStepId[], opts?: { force?: boolean }) => Promise<void>;
  /**
   * Push an authoritative value a caller already holds, skipping the round trip.
   * Used where a mutation response carries the answer — every Brand DNA PUT
   * returns the recomputed `progress`, so re-fetching it would only add latency.
   */
  applySignal: (patch: Partial<SetupSignals>, steps: readonly SetupStepId[]) => void;
  /**
   * Handle a `setup:progress-changed` announcement: optionally flip the step
   * immediately, then confirm against the backend — retrying with backoff while
   * the step is still reported incomplete, so async work (KB indexing) lands
   * without the user having to reload.
   */
  handleChange: (step: SetupStepId, optimistic?: boolean) => void;
  /** Drops every signal and timestamp back to "never fetched". */
  reset: () => void;
}

const initialRecord = <T>(value: T): Record<SetupStepId, T> =>
  SETUP_STEP_IDS.reduce((acc, id) => ({ ...acc, [id]: value }), {} as Record<SetupStepId, T>);

/** In-flight reads, so overlapping revalidations collapse into one request. */
const inFlight = new Map<SetupStepId, Promise<void>>();
/** Pending confirm timers, keyed by step, so a newer action cancels the old chain. */
const confirmTimers = new Map<SetupStepId, ReturnType<typeof setTimeout>>();

export const useSetupStore = create<SetupState & SetupActions>()(
  devtools(
    (set, get) => ({
      signals: { ...EMPTY_SETUP_SIGNALS },
      status: initialRecord<FetchStatus>('idle'),
      fetchedAt: initialRecord<number | null>(null),

      refresh: async (steps = SETUP_STEP_IDS, opts) => {
        const now = Date.now();
        const { fetchedAt } = get();

        // A flagged-off step has no surface and, in Telegram's case, no backend
        // route either — reading its signal would only produce a 404 per load.
        const pending = steps.filter(isStepEnabled).map(step => {
          const existing = inFlight.get(step);
          if (existing) return existing;

          const last = fetchedAt[step];
          if (!opts?.force && last !== null && now - last < SETUP_STALE_AFTER_MS) {
            return Promise.resolve();
          }

          set(
            state => ({ status: { ...state.status, [step]: 'loading' } }),
            false,
            `setup/refresh/${step}`
          );

          const request = SIGNAL_FETCHERS[step]()
            .then(patch => {
              set(
                state => ({
                  signals: { ...state.signals, ...patch },
                  status: { ...state.status, [step]: 'ready' as FetchStatus },
                  fetchedAt: { ...state.fetchedAt, [step]: Date.now() },
                }),
                false,
                `setup/refresh/${step}/success`
              );
            })
            .catch(() => {
              // Best effort: a failed read leaves the step's last known value in
              // place rather than regressing a completed step to incomplete.
              set(
                state => ({
                  status: { ...state.status, [step]: 'error' as FetchStatus },
                  signals: {
                    ...state.signals,
                    [SIGNAL_KEY_BY_STEP[step]]:
                      state.signals[SIGNAL_KEY_BY_STEP[step]] ?? placeholderSignal(step, false),
                  },
                  fetchedAt: { ...state.fetchedAt, [step]: Date.now() },
                }),
                false,
                `setup/refresh/${step}/error`
              );
            })
            .finally(() => {
              inFlight.delete(step);
            });

          inFlight.set(step, request);
          return request;
        });

        await Promise.all(pending);
      },

      applySignal: (patch, steps) => {
        const now = Date.now();
        set(
          state => ({
            signals: { ...state.signals, ...patch },
            status: steps.reduce(
              (acc, step) => ({ ...acc, [step]: 'ready' as FetchStatus }),
              state.status
            ),
            fetchedAt: steps.reduce((acc, step) => ({ ...acc, [step]: now }), state.fetchedAt),
          }),
          false,
          'setup/applySignal'
        );
      },

      handleChange: (step, optimistic = false) => {
        // An action that only ever completes a step tells us nothing new once
        // the step is already complete — bail out rather than re-reading the
        // API on, say, every chat message the user sends.
        if (optimistic && isStepComplete(step, get().signals)) return;

        const timer = confirmTimers.get(step);
        if (timer) clearTimeout(timer);
        confirmTimers.delete(step);

        if (optimistic) {
          set(
            state => ({
              signals: {
                ...state.signals,
                // A minimal "complete" value — the confirming fetch overwrites
                // it with the real one moments later.
                [SIGNAL_KEY_BY_STEP[step]]: placeholderSignal(step, true),
              },
            }),
            false,
            `setup/optimistic/${step}`
          );
        }

        let attempt = 0;
        const confirm = () => {
          void get()
            .refresh([step], { force: true })
            .then(() => {
              if (isStepComplete(step, get().signals)) return;
              if (attempt >= CONFIRM_RETRY_DELAYS_MS.length) return;
              const delay = CONFIRM_RETRY_DELAYS_MS[attempt];
              attempt += 1;
              confirmTimers.set(step, setTimeout(confirm, delay));
            });
        };
        confirm();
      },

      reset: () => {
        inFlight.clear();
        confirmTimers.forEach(clearTimeout);
        confirmTimers.clear();
        set(
          {
            signals: { ...EMPTY_SETUP_SIGNALS },
            status: initialRecord<FetchStatus>('idle'),
            fetchedAt: initialRecord<number | null>(null),
          },
          false,
          'setup/reset'
        );
      },
    }),
    { name: 'setup-store' }
  )
);
